/**
 * What the AI reviewer concluded, in full.
 *
 * The verdict tally is a count of DISTINCT issues, not findings — the summary
 * payload is already grouped by rule code, so a tally over `issues[]` can never
 * claim more opinions than the reviewer actually gave.
 *
 * `ai_confidence` never appears here: it lives only on issue-detail occurrences,
 * and even there it is framed as a model's stated confidence, not a
 * correctness gauge.
 */

import AiStatusChip from './AiStatusChip';
import { agentVerdictTally } from './dqSelectors';
import { VERDICT_ORDER, VERDICT_TOKENS, verdictToken } from './dqTokens';
import type { ValidationSummary } from '../../../types/dataQuality';

export interface AgentReviewPanelProps {
  summary: ValidationSummary;
}

export default function AgentReviewPanel({ summary }: AgentReviewPanelProps) {
  if (!summary.ai_status) return null;

  const tally = agentVerdictTally(summary.issues);
  const total = Object.values(tally).reduce((sum, n) => sum + n, 0);

  return (
    <div className="rounded-xl border border-neutral-200 p-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-neutral-400">
        AI reviewer
      </p>

      {/* The full sentence only. The chip variant was rendered here too, which
          repeated the same icon, colour and model attribution immediately above
          it — it read as a rendering fault rather than as emphasis. */}
      <AiStatusChip summary={summary} variant="full" />

      {total > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] uppercase tracking-wider text-neutral-400">
            Verdicts across {total} distinct issue{total === 1 ? '' : 's'}
          </p>
          <div className="flex h-2 overflow-hidden rounded-full bg-neutral-100">
            {VERDICT_ORDER.filter((v) => tally[v]).map((v) => (
              <div
                key={v}
                className={VERDICT_TOKENS[v].dot}
                style={{ width: `${(tally[v] / total) * 100}%` }}
                title={`${VERDICT_TOKENS[v].label}: ${tally[v]}`}
              />
            ))}
          </div>
          <ul className="mt-2 space-y-1">
            {VERDICT_ORDER.filter((v) => tally[v]).map((v) => {
              const token = verdictToken(v)!;
              return (
                <li key={v} className="flex items-center gap-1.5 text-xs text-neutral-600">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${token.dot}`} />
                  <span className="font-medium text-neutral-800">{tally[v]}</span>
                  {token.label}
                  <span className="text-neutral-400">— {token.effect}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
